import { useEffect, lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';

// Components (always loaded)
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AnalyticsTracker from './components/AnalyticsTracker';

// Critical pages - eagerly loaded (first paint)
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';

// Lazy-loaded pages - split into separate chunks
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const EmailVerificationPage = lazy(() => import('./pages/EmailVerificationPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const BillingHistoryPage = lazy(() => import('./pages/BillingHistoryPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const VerifyPage = lazy(() => import('./pages/VerifyPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FAQPage = lazy(() => import('./pages/FAQPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const CaseStudiesPage = lazy(() => import('./pages/CaseStudiesPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'));
const ResourceArticlePage = lazy(() => import('./pages/ResourceArticlePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const DisclaimerPage = lazy(() => import('./pages/DisclaimerPage'));
const StandardsPage = lazy(() => import('./pages/StandardsPage'));

// SEO Landing Pages
const TopComplianceServicesPage = lazy(() => import('./pages/TopComplianceServicesPage'));
const VisualAccessibilityPage = lazy(() => import('./pages/VisualAccessibilityPage'));
const CostComparisonPage = lazy(() => import('./pages/CostComparisonPage'));
const WcagStandardsGuidePage = lazy(() => import('./pages/WcagStandardsGuidePage'));
const AdaTitleIIBlogPage = lazy(() => import('./pages/AdaTitleIIBlogPage'));

// Loading fallback for lazy-loaded pages
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary-500"></div>
    </div>
  );
}

function App() {
  const { checkAuth, isLoading } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <Suspense fallback={<PageLoader />}>
      <AnalyticsTracker />
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="forgot-password" element={<ForgotPasswordPage />} />
          <Route path="reset-password" element={<ResetPasswordPage />} />
          <Route path="verify-email" element={<EmailVerificationPage />} />
          <Route path="pricing" element={<PricingPage />} />
          <Route path="savings" element={<Navigate to="/pricing" replace />} />
          <Route path="time-savings" element={<Navigate to="/pricing" replace />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="faq" element={<FAQPage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="case-studies" element={<CaseStudiesPage />} />
          <Route path="resources" element={<ResourcesPage />} />
          <Route path="resources/:slug" element={<ResourceArticlePage />} />
          <Route path="security" element={<SecurityPage />} />
          <Route path="terms" element={<TermsPage />} />
          <Route path="privacy" element={<PrivacyPage />} />
          <Route path="disclaimer" element={<DisclaimerPage />} />
          <Route path="standards" element={<StandardsPage />} />
          {/* SEO Landing Pages */}
          <Route path="top-compliance-services" element={<TopComplianceServicesPage />} />
          <Route path="visual-accessibility" element={<VisualAccessibilityPage />} />
          <Route path="cost-comparison" element={<CostComparisonPage />} />
          <Route path="wcag-standards-guide" element={<WcagStandardsGuidePage />} />
          <Route path="ada-title-ii-pdf-compliance" element={<AdaTitleIIBlogPage />} />
          <Route path="verify" element={<VerifyPage />} />
          <Route path="verify/:code" element={<VerifyPage />} />
        </Route>

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="documents" element={<DocumentsPage />} />
          <Route path="account" element={<AccountPage />} />
          <Route path="billing" element={<BillingHistoryPage />} />
        </Route>

        {/* Admin route (no layout, has its own auth) */}
        <Route path="/admin" element={<AdminPage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}

export default App;
